import type { ReactNode } from 'react'
import { AlertTriangle, Info, Lock } from 'lucide-react'
import { cn } from '@/lib/cn'

type NoticeProps = {
  tone?: 'info' | 'warning' | 'danger' | 'locked'
  title: string
  children?: ReactNode
  action?: ReactNode
  className?: string
}

/** Block message for empty / error / forbidden states. */
export function Notice({ tone = 'info', title, children, action, className }: NoticeProps) {
  const Icon = tone === 'locked' ? Lock : tone === 'info' ? Info : AlertTriangle
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn(
        'flex gap-3 rounded-xl border p-4 text-sm',
        tone === 'danger' && 'border-danger/30 bg-danger-soft',
        tone === 'warning' && 'border-warning/30 bg-warning-soft',
        (tone === 'info' || tone === 'locked') && 'border-line bg-panel',
        className,
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-ink-muted',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink">{title}</p>
        {children && <div className="mt-1 text-ink-muted">{children}</div>}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </div>
  )
}
